import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Logo4dCeramics from '#app/components/logo-4d-ceramics.tsx'
import LogoActiveSurfaces from '#app/components/logo-active-surfaces.tsx'
import LogoHypertouch from '#app/components/logo-hypertouch.tsx'
import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import { BASE_URL } from '#app/utils/config.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { LocalizedLink, useLocale } from '#app/utils/i18n.tsx'
import { generateMetaTranslationLoader } from '#app/utils/i18next.server.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'
import { type Route } from '../../../../.react-router/types/app/routes/($lang)+/_marketing+/+types/sustainability.ts'

export async function loader({ request }: Route.LoaderArgs) {
	return {
		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'innovation',
			keys: {
				title: 'hero.title',
				description: 'hero.subtitle',
				canonical: `${BASE_URL}${routes.public.landings.innovation.route({})}`,
			},
		}),
	}
}

export const meta = generateTranslatedMeta

export default function ComponentPage() {
	const { t } = useTranslation('innovation')

	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: t('hero.title'),
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const locale = useLocale()

	return (
		<>
			<LandingSection backgroundColor={extendedTheme.colors.brand.dusk.DEFAULT}>
				<div className={'flex flex-col gap-y-[15vh]'}>
					<Logo4dCeramics
						className={'mx-auto h-[10vh] text-white'}
						key={'4d-ceramics'}
					/>
					<div className={'grid grid-cols-12 grid-rows-9 gap-sm'}>
						<div className={'col-start-8 -col-end-2 row-start-1 row-end-4'}>
							<MarketingImage
								src={'/img/what-is-sapienstone/dimensions/1-borde-convexo.webp'}
							/>
						</div>
						<div className={'col-start-2 -col-end-4 row-start-3 -row-end-1'}>
							<div
								className={cn(
									'text-white',
									'flex flex-col justify-between gap-xl border-l border-white pl-md xl:gap-[10vh] xl:pl-lg',
								)}
							>
								<div className={'flex flex-col gap-md'}>
									<Text
										className={'max-w-lg uppercase'}
										type={'serif'}
										weight={'light'}
										size={'h3'}
										as={'h2'}
									>
										{t('fourD.subtitle')}
									</Text>
									<Text
										className={'max-w-lg'}
										as={'p'}
										size={'small'}
										weight={'light'}
									>
										{t('fourD.description')}
									</Text>
									<Button
										asChild
										variant={'link'}
										className={
											'w-fit items-start px-0 text-start text-body-2xs lowercase text-brand-earth xl:text-body-xs'
										}
									>
										<LocalizedLink
											to={routes.public.landings.fourthDimension.route({})}
										>
											{t('common:actions.viewMore')}
										</LocalizedLink>
									</Button>
								</div>
								<MarketingImage
									alt={'Thickness'}
									imageClassName={
										'h-full max-h-[50vh] w-full object-cover object-[50%,100%]'
									}
									src={
										'/img/what-is-sapienstone/dimensions/SAPIENSTONE_4D_1.webp'
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</LandingSection>

			<LandingSection
				backgroundColor={extendedTheme.colors.brand.aquamarine.DEFAULT}
			>
				<div className={'flex flex-col gap-y-[15vh]'}>
					<LogoHypertouch className={'h-[50px]'} key={'hypertouch'} />
					<div className={'grid grid-cols-12 grid-rows-7 gap-sm'}>
						<div className={'col-start-8 -col-end-2 row-start-1 row-end-5'}>
							<MarketingImage
								src={
									'/img/what-is-sapienstone/hypertouch/icg_hypertouch_2022 (9).webp'
								}
							/>
						</div>
						<div className={'col-start-2 col-end-7 row-start-3 -row-end-1'}>
							<div
								className={cn(
									'flex flex-col justify-between gap-xl border-l border-current pl-md xl:gap-[10vh] xl:pl-lg',
								)}
							>
								<div className={'flex flex-col gap-md'}>
									<Text
										className={'max-w-lg uppercase'}
										type={'serif'}
										weight={'light'}
										size={'h3'}
										as={'h2'}
									>
										{t('hypertouch.subtitle')}
									</Text>
									<Text
										className={'max-w-lg'}
										as={'p'}
										size={'small'}
										weight={'light'}
									>
										{t('hypertouch.description')}
									</Text>
									<Button
										asChild
										variant={'link'}
										className={
											'w-fit items-start px-0 text-start text-body-2xs lowercase text-brand-earth xl:text-body-xs'
										}
									>
										<a
											target={'_blank'}
											rel={'noreferrer noopener'}
											href={
												locale === 'it'
													? 'https://www.irisceramicagroup.com/innovazione/hypertouch/'
													: `https://www.irisceramicagroup.com/en/innovation/hypertouch/`
											}
										>
											{t('common:actions.viewMore')}
										</a>
									</Button>
								</div>
								<MarketingImage
									alt={'Thickness'}
									imageClassName={
										'h-full max-h-[50vh] w-full object-cover object-[50%,50%]'
									}
									src={
										'/img/what-is-sapienstone/hypertouch/icg_hypertouch_2022 (15).webp'
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</LandingSection>

			<LandingSection backgroundColor={extendedTheme.colors.brand.mist.DEFAULT}>
				<div className={'flex flex-col gap-y-[15vh]'}>
					<LogoActiveSurfaces className={'h-[10vh]'} key={'active-surfaces'} />

					<div className={'grid grid-cols-12 grid-rows-9 gap-sm'}>
						<div
							className={
								'relative col-start-2 col-end-5 row-start-3 -row-end-3'
							}
						>
							<MarketingImage
								className={'h-full max-h-[30vh]'}
								imageClassName={'h-full object-cover '}
								src={
									'/img/what-is-sapienstone/active-surfaces/FOTO BOI 2-1.webp'
								}
							/>
						</div>
						<div className={'col-start-6 -col-end-2 row-start-1 -row-end-1'}>
							<div
								className={cn(
									'flex flex-col justify-between gap-xl border-l border-current pl-md xl:gap-[10vh] xl:pl-lg',
								)}
							>
								<div className={'flex flex-col gap-md'}>
									<Text
										className={'max-w-lg uppercase'}
										type={'serif'}
										weight={'light'}
										size={'h3'}
										as={'h2'}
									>
										"{t('active.subtitle')}”
									</Text>
									<Text
										className={'max-w-lg'}
										as={'p'}
										size={'small'}
										weight={'light'}
									>
										{t('active.description')}
									</Text>
									<Button
										asChild
										variant={'link'}
										className={
											'w-fit items-start px-0 text-start text-body-2xs lowercase text-brand-earth xl:text-body-xs'
										}
									>
										<a
											target={'_blank'}
											rel={'noreferrer noopener'}
											href={`https://www.active-surfaces.com${locale === 'it' ? '' : `/${locale}/`}`}
										>
											{t('common:actions.viewMore')}
										</a>
									</Button>
								</div>
								<MarketingImage
									alt={
										'Iris Ceramica Group Showroom Gallery London Active Exhibition'
									}
									imageClassName={
										'h-full max-h-[80vh] w-full object-cover object-[50%,100%]'
									}
									src={
										'/img/what-is-sapienstone/active-surfaces/icg-showroom_gallery-london-gb-2023-ref3_part22-level_0_active_exhibition.webp'
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</LandingSection>
		</>
	)
}
